const COLOURS = ['#ffcb2f', '#c9e9f5', '#f9d3e1', '#d6efc8', '#e4d9f7', '#ffe0c2'];

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0]?.[0]}${words[1]?.[0]}` : name.trim().slice(0, 2);
  return letters.toUpperCase() || '?';
}

/** The same name always gets the same colour. */
function colour(name: string): string {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) | 0;
  return COLOURS[Math.abs(hash) % COLOURS.length] ?? '#e4e1ee';
}

const SIZES = {
  xs: 'size-[26px] text-[10px]',
  sm: 'size-8 text-[11px]',
  md: 'size-10 text-[13px]',
  lg: 'size-11 text-sm',
  xl: 'size-[72px] text-2xl',
} as const;

/** Initials in a coloured circle (profile pictures come later). Decorative: names sit beside it. */
export function Avatar({
  name,
  size = 'md',
  ring = false,
}: {
  name: string;
  size?: keyof typeof SIZES;
  ring?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      style={{ background: colour(name) }}
      className={`flex shrink-0 items-center justify-center rounded-full font-bold text-ink ${SIZES[size]} ${ring ? 'border-2 border-card' : ''}`}
    >
      {initials(name)}
    </span>
  );
}

/** Overlapping avatars of a few members. */
export function AvatarStack({ names, size = 'sm' }: { names: string[]; size?: 'sm' | 'xs' }) {
  return (
    <span aria-hidden="true" className="flex">
      {names.map((name, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: names can repeat, and the list never reorders
        <span key={i} className={i ? '-ml-2' : ''}>
          <Avatar name={name} size={size} ring />
        </span>
      ))}
    </span>
  );
}

/** The community's icon tile: its initials on yellow (game icons come with publishing). */
export function CommunityIcon({ name, size = 'lg' }: { name: string; size?: 'lg' | 'sm' }) {
  const classes =
    size === 'lg'
      ? 'size-[68px] rounded-[18px] border-[3px] border-card text-[22px] shadow-[0_2px_8px_rgba(27,21,47,0.12)] sm:size-[104px] sm:rounded-[26px] sm:border-4 sm:text-4xl'
      : 'size-[34px] rounded-[10px] text-sm';
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center bg-brand font-display font-bold text-ink ${classes}`}
    >
      {initials(name)}
    </span>
  );
}

const DOTS = [
  ['8%', '28%', 18, '#7ad7f0'],
  ['22%', '62%', 10, '#f58ab0'],
  ['41%', '20%', 14, '#ffcb2f'],
  ['57%', '55%', 22, '#7ad7f0'],
  ['73%', '25%', 12, '#9be27a'],
  ['88%', '58%', 16, '#ffcb2f'],
] as const;

/** The dark banner with coloured dots, like falling stars. */
export function Banner({ className = 'h-[120px] sm:h-[168px]' }: { className?: string }) {
  return (
    <div aria-hidden="true" className={`relative overflow-hidden bg-ink ${className}`}>
      {DOTS.map(([left, top, size, background]) => (
        <span
          key={left}
          className="absolute rounded-full"
          style={{ left, top, width: size, height: size, background }}
        />
      ))}
    </div>
  );
}
